import { connect } from 'formik';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { P } from '../typography/typography';
import { StyledErrorMessage } from './formikFieldWrapper.styles';
import { Label } from './label';

export type Props = {
	name: string;
	label?: string;
	smallLabel?: boolean;
	formik?: any;
	languageComponent: string;
	marginBottom?: string;
	marginTop?: string;
	infoText?: string;
	children?: ReactNode;
	// When true, the error message always reserves one line of space (even without an error),
	// so the layout doesn't shift when an error appears.
	reserveErrorSpace?: boolean;
};

const StyledWrapper = styled.div`
	margin-bottom: ${({ marginBottom }) => marginBottom || 0};
	margin-top: ${({ marginTop }) => marginTop || 0};
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox */
	input[type='number'] {
		-moz-appearance: textfield;
	}
`;

const FormikFieldWrapper: FC<Props> = ({
	name,
	label,
	smallLabel = false,
	children,
	formik,
	languageComponent,
	marginBottom,
	marginTop,
	infoText,
	reserveErrorSpace = false,
}) => {
	const { t } = useTranslation(languageComponent);
	const { errors, touched } = formik;
	let error = errors[name];
	let isTouched = touched[name];

	// When dealing with field arrays we have to bury down into the error object
	if (name.includes('.')) {
		// Split the name up from each dot indicator and use that to trace down
		const values = name.split('.');

		for (let i = 0; i < values.length; ++i) {
			const val = values[i];

			// If we're on the first value we get the top level
			// component from the errors object
			if (i === 0) {
				error = errors[val];
				isTouched = touched[val];
			} else if (error) {
				// Otherwise if we have a value we trace down that object
				// until we've cleared the array and found the error message
				error = error[val];

				if (isTouched) {
					isTouched = isTouched[val];
				}
			}
		}
	}
	return (
		<StyledWrapper marginBottom={marginBottom} marginTop={marginTop}>
			{label && (
				<Label small={smallLabel} htmlFor={name}>
					{label}
				</Label>
			)}
			{children}
			{reserveErrorSpace ? (
				<StyledErrorMessage reserveSpace>{error && isTouched ? t(error) : ''}</StyledErrorMessage>
			) : (
				error && isTouched && <StyledErrorMessage>{t(error)}</StyledErrorMessage>
			)}
			{infoText && <P style={{ margin: '1rem' }}>{`${t(infoText)}`}</P>}
		</StyledWrapper>
	);
};

export default connect(FormikFieldWrapper);
